import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
  Building2, Phone, Mail, CreditCard, Calendar,
  Plus, Search, Filter, Eye, MessageCircle, FileText,
  Download, Package, ShoppingCart, RefreshCw, X,
  CheckCircle, Clock, Truck, AlertCircle, UserCheck, XCircle,
  Scissors, Sparkles, Receipt, FileCheck, Edit, Paperclip
} from 'lucide-react';
import { AuthContext } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import IncidenciaDetalleModal from '../components/IncidenciaDetalleModal';

const API_URL = process.env.REACT_APP_API_URL;

export default function ClientManagementCli() {
  const { user } = useContext(AuthContext);
  // ─── TEMA DINÁMICO ───────────────────────────────────────────────────
  const { theme } = useTheme();
  // theme.primary, theme.secondary, theme.primaryDark,
  // theme.primaryLight, theme.primaryBorder
  // ─────────────────────────────────────────────────────────────────────

  const [activeClient, setActiveClient] = useState(null);
  const [linkedClients, setLinkedClients] = useState([]);
  
  const [clinicasDisponibles, setClinicasDisponibles] = useState({
    principal: { matriz: null, asociadas: [] },
    adicionales: []
  });
  const [clinicasAdicionalesActivas, setClinicasAdicionalesActivas] = useState([]);
  const [clinicaMatrizActiva, setClinicaMatrizActiva] = useState(null);
  
  const [pedidos, setPedidos] = useState([]);
  const [pedidosPendientes, setPedidosPendientes] = useState([]);
  const [pedidosPendientesHija, setPedidosPendientesHija] = useState([]);
  const [facturasPendientes, setFacturasPendientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingCatalogo, setLoadingCatalogo] = useState(false);
  // Catálogo: modal de filtros (proveedores + categorías). Vacío = todos.
  const [showCatalogoModal, setShowCatalogoModal] = useState(false);
  const [catProviders, setCatProviders] = useState([]);
  const [selProvIds, setSelProvIds] = useState([]);
  const [selCategorias, setSelCategorias] = useState([]);
  
  const [filtros, setFiltros] = useState({ npedido: '', fecha: '', estado: '', cliente: '' });
  
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [comentarios, setComentarios] = useState([]);
  const [nuevoComentario, setNuevoComentario] = useState('');
  
  const [selectedPedidos, setSelectedPedidos] = useState([]);
  const [estadoMasivo, setEstadoMasivo] = useState('');

  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [pedidoAprobacion, setPedidoAprobacion] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [selectedPedidosAprobacion, setSelectedPedidosAprobacion] = useState([]);

  const [showInvoicePreviewModal, setShowInvoicePreviewModal] = useState(false);
  const [invoicePreviewData, setInvoicePreviewData] = useState(null);
  const [loadingInvoicePreview, setLoadingInvoicePreview] = useState(false);
  const [confirmingInvoice, setConfirmingInvoice] = useState(false);

  // La pestaña activa se persiste en sessionStorage (igual que activeClient) para
  // que sobreviva a los remounts del componente. ClientManagementCli vive bajo
  // <ProtectedRoute>, que desmonta TODO su árbol cuando ThemeContext.themeReady
  // pasa momentáneamente a false (ThemeProvider hace setThemeReady(false) al
  // recargar el tema). Cualquier recarga de tema / cambio de identidad de `user`
  // remonta el subárbol y, sin persistencia, reinicia vistaActiva a 'mis-pedidos'
  // ~1s después (justo el síntoma reportado al entrar en "Incidencias").
  const [vistaActiva, setVistaActiva] = useState(
    () => sessionStorage.getItem('vistaActiva') || 'mis-pedidos'
  );

  // ── Incidencias (la clínica registra y ve las suyas) ──────────────────────
  const [incidencias, setIncidencias] = useState([]);
  const [incLoading, setIncLoading]   = useState(false);
  const [incSaving, setIncSaving]     = useState(false);
  const [incForm, setIncForm]         = useState({ nombre: '', descripcion: '', prioridad: 'media' });
  const [incArchivos, setIncArchivos] = useState([]);   // File[] pendientes de subir
  const [incMsg, setIncMsg]           = useState('');
  const [incVista, setIncVista]       = useState('tablero'); // 'tablero' | 'tabla'
  const [incDragId, setIncDragId]     = useState(null);
  const [incDropCol, setIncDropCol]   = useState(null);
  // Ficha ampliada. null = cerrada. Se abre con el objeto del listado y el modal
  // se enriquece solo pidiendo la ficha completa.
  const [incDetalle, setIncDetalle]   = useState(null);
  // Posición del puntero al pulsar una tarjeta: sirve para distinguir un click
  // de un arrastre. La tarjeta es draggable, así que un onClick a secas también
  // dispararía al soltar un arrastre que termina sobre la propia tarjeta.
  const incPressPos = useRef(null);
  const INC_UMBRAL_CLICK = 5; // px
  // Panel de red (solo clínica matriz)
  const [esMatriz, setEsMatriz]       = useState(false);
  const [incSeccion, setIncSeccion]   = useState('incidencias'); // 'incidencias' | 'panel'
  const [incStats, setIncStats]       = useState(null);
  const [incStatsLoading, setIncStatsLoading] = useState(false);
  const _fmtISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [incRepDesde, setIncRepDesde] = useState(_fmtISO(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [incRepHasta, setIncRepHasta] = useState(_fmtISO(new Date()));

  const fetchIncidencias = async (cliente = activeClient) => {
    setIncLoading(true);
    try {
      const token = localStorage.getItem('token');
      // La MATRIZ (clinica_matriz 0/null) ve TODAS las de su red; una sucursal, solo la suya.
      const esMatrizSel = !!cliente && (cliente.clinica_matriz === 0 || cliente.clinica_matriz == null);
      const qs = (cliente && !esMatrizSel) ? `?cliente_id=${cliente.id}` : '';
      const r = await fetch(`${API_URL}/api/incidencias/mias${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setIncidencias(Array.isArray(d.incidencias) ? d.incidencias : []);
    } catch (e) { setIncidencias([]); }
    finally { setIncLoading(false); }
  };

  // ── Adjuntos de la incidencia (imágenes) ────────────────────────────────
  // El backend acepta hasta 10 ficheros en el campo `archivos` (multer), 15MB
  // cada uno. Se descartan duplicados por nombre+tamaño para que reabrir el
  // selector y volver a elegir la misma foto no la suba dos veces.
  const INC_MAX_ARCHIVOS = 10;
  const INC_MAX_BYTES = 15 * 1024 * 1024;

  const anadirIncArchivos = (fileList) => {
    const nuevos = Array.from(fileList || []);
    const grandes = nuevos.filter(f => f.size > INC_MAX_BYTES);
    if (grandes.length) {
      setIncMsg(`Cada imagen debe pesar menos de 15MB: ${grandes.map(f => f.name).join(', ')}`);
    }
    const validos = nuevos.filter(f => f.size <= INC_MAX_BYTES);
    setIncArchivos(prev => {
      const clave = (f) => `${f.name}|${f.size}`;
      const yaEstan = new Set(prev.map(clave));
      const sinRepetir = validos.filter(f => !yaEstan.has(clave(f)));
      const total = [...prev, ...sinRepetir];
      if (total.length > INC_MAX_ARCHIVOS) {
        setIncMsg(`Máximo ${INC_MAX_ARCHIVOS} imágenes por incidencia`);
        return total.slice(0, INC_MAX_ARCHIVOS);
      }
      return total;
    });
  };

  const quitarIncArchivo = (idx) => setIncArchivos(prev => prev.filter((_, i) => i !== idx));

  // Las previews se generan una sola vez por lista de ficheros. Sin el useMemo,
  // cada tecleo en el formulario re-renderiza, crea blobs nuevos y las
  // miniaturas parpadean (ademas de fugar los anteriores).
  const incPreviews = useMemo(
    () => incArchivos.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
    [incArchivos],
  );
  useEffect(() => () => incPreviews.forEach((p) => URL.revokeObjectURL(p.url)), [incPreviews]);

  const crearIncidencia = async () => {
    if (!incForm.nombre.trim()) { setIncMsg('El título es obligatorio'); return; }
    setIncSaving(true); setIncMsg('');
    try {
      const token = localStorage.getItem('token');
      const payload = { ...incForm, cliente_id: activeClient?.id };

      // Con adjuntos va como multipart. OJO: no se pone Content-Type a mano —
      // el navegador tiene que poner el boundary él solo o multer no parsea
      // nada y llegan 0 ficheros. Sin adjuntos, JSON de toda la vida.
      let opciones;
      if (incArchivos.length) {
        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => fd.append(k, v ?? ''));
        incArchivos.forEach(f => fd.append('archivos', f));
        opciones = { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd };
      } else {
        opciones = {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        };
      }

      const r = await fetch(`${API_URL}/api/incidencias/mias`, opciones);
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Error al registrar la incidencia');
      setIncForm({ nombre: '', descripcion: '', prioridad: 'media' });
      setIncArchivos([]);
      setIncMsg('Incidencia registrada correctamente');
      fetchIncidencias();
    } catch (e) { setIncMsg(e.message || 'Error al registrar la incidencia'); }
    finally { setIncSaving(false); }
  };

  // ── Tablero kanban de la red (matriz ve todas sus clínicas; hija solo la suya) ──
  const INC_COLS = [
    { key: 'abierta',    label: 'Abierta',    color: '#0ea5e9' },
    { key: 'en_proceso', label: 'En proceso', color: '#f59e0b' },
    { key: 'resuelta',   label: 'Resuelta',   color: '#10b981' },
    { key: 'cerrada',    label: 'Cerrada',    color: '#9ca3af' },
  ];
  const INC_PRIO_COL = { critica: '#f43f5e', alta: '#f59e0b', media: '#0ea5e9', baja: '#9ca3af' };
  const EST_COL = { abierta: '#0ea5e9', en_proceso: '#f59e0b', resuelta: '#10b981', cerrada: '#9ca3af' };
  const capit = (s) => String(s || '—').replace(/_/g, ' ').replace(/^\w/, (m) => m.toUpperCase());
  const barBlock = (title, rows) => {
    const max = Math.max(1, ...rows.map((r) => r.value));
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h4 className="text-sm font-bold text-gray-700 mb-3">{title}</h4>
        {rows.length === 0 ? <p className="text-xs text-gray-400">Sin datos.</p> : (
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-gray-600 w-28 flex-shrink-0 truncate">{r.label}</span>
                <div className="flex-1 h-3 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(r.value / max) * 100}%`, backgroundColor: r.color, minWidth: r.value > 0 ? '6px' : '0' }}/>
                </div>
                <span className="text-xs font-bold text-gray-500 w-8 text-right tabular-nums">{r.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const cambiarEstadoIncCli = async (inc, nuevoEstado) => {
    if (!inc || inc.estado === nuevoEstado) return;
    const anterior = inc.estado;
    const fechaResAnterior = inc.fecha_resolucion;
    const cierra = nuevoEstado === 'resuelta' || nuevoEstado === 'cerrada';
    setIncidencias(prev => prev.map(x => x.id === inc.id
      ? { ...x, estado: nuevoEstado,
          fecha_resolucion: cierra ? (x.fecha_resolucion || new Date().toISOString().slice(0, 10)) : null }
      : x));
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/incidencias/mias/${inc.id}/estado`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado: nuevoEstado }),
      });
      if (!r.ok) throw new Error();
    } catch {
      setIncidencias(prev => prev.map(x => x.id === inc.id ? { ...x, estado: anterior, fecha_resolucion: fechaResAnterior } : x));
      setIncMsg('No se pudo cambiar el estado');
    }
  };

  const fetchIncContexto = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/incidencias/mias/contexto`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setEsMatriz(!!d.esMatriz);
      if (!d.esMatriz) setIncSeccion('incidencias');
    } catch { setEsMatriz(false); }
  };

  const fetchIncStats = async () => {
    setIncStatsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/incidencias/mias/estadisticas`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      setIncStats(await r.json());
    } catch { setIncStats(null); }
    finally { setIncStatsLoading(false); }
  };

  const descargarReporteMias = async (formato) => {
    try {
      const token = localStorage.getItem('token');
      const p = new URLSearchParams({ formato });
      if (incRepDesde) p.append('desde', incRepDesde);
      if (incRepHasta) p.append('hasta', incRepHasta);
      const r = await fetch(`${API_URL}/api/incidencias/mias/reporte?${p}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_incidencias_${incRepDesde}_${incRepHasta}.${formato === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { setIncMsg('No se pudo descargar el reporte'); }
  };

  const estadoIncBadge = (estado) => {
    const map = {
      abierta:    { t: 'Abierta',    c: 'bg-blue-50 text-blue-700' },
      en_proceso: { t: 'En proceso', c: 'bg-amber-50 text-amber-700' },
      resuelta:   { t: 'Resuelta',   c: 'bg-green-50 text-green-700' },
      cerrada:    { t: 'Cerrada',    c: 'bg-gray-100 text-gray-600' },
    };
    const s = map[estado] || { t: estado || '—', c: 'bg-gray-100 text-gray-600' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.c}`}>{s.t}</span>;
  };

  const prioridadIncBadge = (p) => {
    const map = { baja: 'bg-gray-100 text-gray-600', media: 'bg-blue-50 text-blue-700', alta: 'bg-amber-50 text-amber-700', critica: 'bg-red-50 text-red-700' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[p] || 'bg-gray-100 text-gray-600'}`}>{p ? p.charAt(0).toUpperCase() + p.slice(1) : '—'}</span>;
  };

  // ── Gestión de albaranes ──────────────────────────────────────────────
  const [pedidosRecibidos, setPedidosRecibidos] = useState([]);
  const [loadingRecibidos, setLoadingRecibidos] = useState(false);
  const [filtroAlbaranes, setFiltroAlbaranes] = useState({ fechaDesde: '', fechaHasta: '', pedido: '' });
  const [descargandoZip, setDescargandoZip] = useState(false);

  // Clínicas del usuario con token activo (el admin se lo envió). null = aún no
  // cargado. La usamos para decidir si mostramos la sección "API Keys".

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);


  // ─── HELPERS DE TEMA ─────────────────────────────────────────────────
  /** Estilo inline para fondos primarios */
  const bgPrimary = { backgroundColor: theme.primary };
  /** Estilo inline para fondos primarios claros */
  const bgPrimaryLight = { backgroundColor: theme.primaryLight };
  /** Estilo inline para bordes primarios */
  const borderPrimary = { borderColor: theme.primary };
  /** Clase de texto con color primario (inline) */
  const textPrimary = { color: theme.primary };
  /** Estilo hover para botones primarios */
  const btnPrimary = {
    backgroundColor: theme.primary,
    transition: 'background-color 0.2s',
  };
  // ─────────────────────────────────────────────────────────────────────

  const puedeVerFacturacion = () => {
    if (!activeClient) return false;
    return [131, 999999].includes(activeClient.id) || 
           [131, 999999].includes(activeClient.clinica_matriz);
  };

  const getTipoBadge = (tipo, clienteId, clinicaMatriz) => {
    if (![131, 999999].includes(clienteId) && ![131, 999999].includes(clinicaMatriz)) return null;
    if (tipo === 'cirugia_plastica') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
          <Scissors size={12} className="mr-1" />Cirugía Plástica
        </span>
      );
    }
    if (tipo === 'medicina_estetica') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <Sparkles size={12} className="mr-1" />Medicina Estética
        </span>
      );
    }
    return null;
  };

  const puedeAprobarPedidos = () => {
    if (!user) return false;
    if (user.permisos === 1) return true;
    if (activeClient) return activeClient.clinica_matriz === 0 || activeClient.clinica_matriz === null;
    return clinicasAdicionalesActivas.some(c => c.clinica_matriz === 0 || c.clinica_matriz === null);
  };


  // El flujo de aprobación (pestaña "Pendientes de aprobación") solo está activo
  // si la CLÍNICA MATRIZ tiene pedidos_pendientes = true. El flag de la matriz
  // gobierna a toda su familia (matriz + hijas).
  const aprobacionActiva = puedeAprobarPedidos() && !!clinicaMatrizActiva?.pedidos_pendientes;

  // Pestaña de pendientes para clínicas NO matriz (hijas): ven sus propios
  // pedidos en estado 6 y pueden editarlos (NO aprobar/rechazar, eso es la matriz).
  // Se muestra si el cliente activo es una hija (tiene matriz) Y o bien tiene
  // pedidos pendientes, o sabemos que su matriz tiene el flujo activo. Nota: un
  // usuario hija NO ve el flag de su matriz (no la tiene entre sus clínicas
  // accesibles), por eso la señal fiable es que existan pedidos pendientes suyos.
  const esClienteHija = !!activeClient && activeClient.clinica_matriz != null && activeClient.clinica_matriz > 0;
  const hijaConPendientes = esClienteHija && (
    pedidosPendientesHija.length > 0 || !!clinicaMatrizActiva?.pedidos_pendientes
  );

  const pedidoRequiereAprobacion = (pedido) => pedido.estado === 6;
  const pedidoEstaRechazado = (pedido) => pedido.estado === 7;
  const puedeModificarEstado = (pedido) => {
    if (!pedido) return false;
    return pedido.estado !== 6 && pedido.estado !== 7;
  };

  const clearActiveClient = () => {
    setActiveClient(null);
    sessionStorage.removeItem('activeClient');
  };

  const loadAvailableClients = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No hay token de autenticación');
      
      const response = await fetch(`${API_URL}/api/clientes/disponibles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al obtener clínicas disponibles');
      
      const data = await response.json();
      setClinicasDisponibles(data.agrupadas);
      
      const adicionales = [];
      data.agrupadas.adicionales.forEach(grupo => {
        if (grupo.matriz) adicionales.push(grupo.matriz);
        adicionales.push(...grupo.asociadas);
      });
      setClinicasAdicionalesActivas(adicionales);
      
      const canApprove = user.permisos === 1 || 
        (data.agrupadas.principal?.matriz?.clinica_matriz === 0) ||
        adicionales.some(c => c.clinica_matriz === 0);
      if (canApprove) fetchPedidosPendientes();
      
      const savedClientData = sessionStorage.getItem('activeClient');
      let clienteASeleccionar = null;
      
      if (savedClientData) {
        try {
          const savedClient = JSON.parse(savedClientData);
          const clienteEncontrado = 
            data.agrupadas.principal.matriz?.id == savedClient.id ? data.agrupadas.principal.matriz :
            data.agrupadas.principal.asociadas.find(c => c.id == savedClient.id) ||
            data.agrupadas.adicionales.flatMap(g => [g.matriz, ...g.asociadas]).find(c => c?.id == savedClient.id);
          if (clienteEncontrado) clienteASeleccionar = clienteEncontrado;
        } catch {
          sessionStorage.removeItem('activeClient');
        }
      }

      if (!clienteASeleccionar && data.agrupadas.principal?.matriz) {
        clienteASeleccionar = data.agrupadas.principal.matriz;
      }

      if (clienteASeleccionar) {
        setActiveClient(clienteASeleccionar);
        sessionStorage.setItem('activeClient', JSON.stringify(clienteASeleccionar));
        if (clienteASeleccionar.clinica_matriz === 0 || clienteASeleccionar.clinica_matriz === null) {
          setClinicaMatrizActiva(clienteASeleccionar);
        } else {
          const matrizCorrespondiente = data.agrupadas.principal.matriz?.id === clienteASeleccionar.clinica_matriz 
            ? data.agrupadas.principal.matriz
            : data.agrupadas.adicionales.find(g => g.matriz?.id === clienteASeleccionar.clinica_matriz)?.matriz;
          setClinicaMatrizActiva(matrizCorrespondiente);
        }
        await fetchLinkedClients(clienteASeleccionar.id, clienteASeleccionar.clinica_matriz);
        await fetchPedidos(clienteASeleccionar.id);
        fetchPedidosPendientesHija(clienteASeleccionar.id);
        if ([131, 999999].includes(clienteASeleccionar.id) || [131, 999999].includes(clienteASeleccionar.clinica_matriz)) {
          await fetchFacturasPendientes();
        }
      }
    } catch (error) {
      console.error('Error al cargar clínicas disponibles:', error);
      setError('Error al cargar las clínicas disponibles');
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = async (cliente) => {
    try {
      setLoading(true);
      setActiveClient(cliente);
      sessionStorage.setItem('activeClient', JSON.stringify(cliente));
      if (cliente.clinica_matriz === 0 || cliente.clinica_matriz === null) {
        setClinicaMatrizActiva(cliente);
      } else {
        const matrizCorrespondiente = clinicasDisponibles.principal.matriz?.id === cliente.clinica_matriz 
          ? clinicasDisponibles.principal.matriz
          : clinicasDisponibles.adicionales.find(g => g.matriz?.id === cliente.clinica_matriz)?.matriz;
        setClinicaMatrizActiva(matrizCorrespondiente);
      }
      await fetchLinkedClients(cliente.id, cliente.clinica_matriz);
      await fetchPedidos(cliente.id);
      fetchPedidosPendientesHija(cliente.id);
      if ([131, 999999].includes(cliente.id) || [131, 999999].includes(cliente.clinica_matriz)) {
        await fetchFacturasPendientes();
      }
      setFiltros({ npedido: '', fecha: '', estado: '', cliente: '' });
      setSelectedPedidos([]);
      setVistaActiva('mis-pedidos');
    } catch (error) {
      console.error('Error al seleccionar cliente:', error);
      setError('Error al seleccionar la clínica');
    } finally {
      setLoading(false);
    }
  };

  const handleMatrizSelect = async (matriz) => {
    setClinicaMatrizActiva(matriz);
    const centrosDeEstaMatriz = matriz.id === clinicasDisponibles.principal.matriz?.id 
      ? [clinicasDisponibles.principal.matriz, ...clinicasDisponibles.principal.asociadas]
      : clinicasDisponibles.adicionales.find(g => g.matriz?.id === matriz.id)?.asociadas || [];
    if (centrosDeEstaMatriz.length > 0 && (!activeClient || (activeClient.clinica_matriz !== matriz.id && activeClient.id !== matriz.id))) {
      await handleClientSelect(centrosDeEstaMatriz[0]);
    }
  };

  const loadClientData = async (clientId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No hay token de autenticación');
      const response = await fetch(`${API_URL}/api/clientes/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al obtener datos del cliente');
      const data = await response.json();
      const client = data.cliente;
      setActiveClient(client);
      fetchLinkedClients(client.id, client.clinica_matriz);
      fetchPedidos(client.id);
      if ([131, 999999].includes(client.id) || [131, 999999].includes(client.clinica_matriz)) fetchFacturasPendientes();
    } catch (error) {
      console.error('Error al cargar cliente:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) loadAvailableClients(); }, [user]);

  // Persistimos la pestaña activa para que sobreviva a los remounts (ver comentario
  // en la declaración de vistaActiva). handleClientSelect sigue reseteando a
  // 'mis-pedidos' al cambiar de centro, y ese reset también queda persistido.
  useEffect(() => { sessionStorage.setItem('vistaActiva', vistaActiva); }, [vistaActiva]);

  // Al cambiar de centro (o entrar a Incidencias), recargar SOLO las de ese centro.
  useEffect(() => {
    if (vistaActiva === 'incidencias' && activeClient?.id) fetchIncidencias(activeClient);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClient?.id, vistaActiva]);

  const getCentrosAsociados = () => {
    if (!clinicaMatrizActiva) return [];
    if (clinicaMatrizActiva.id === clinicasDisponibles.principal.matriz?.id) {
      return [clinicasDisponibles.principal.matriz, ...clinicasDisponibles.principal.asociadas];
    }
    const grupoAdicional = clinicasDisponibles.adicionales.find(g => g.matriz?.id === clinicaMatrizActiva.id);
    return grupoAdicional ? [grupoAdicional.matriz, ...grupoAdicional.asociadas] : [];
  };

  const getTodasLasMatrices = () => {
    const matrices = [];
    if (clinicasDisponibles.principal.matriz) matrices.push(clinicasDisponibles.principal.matriz);
    clinicasDisponibles.adicionales.forEach(grupo => { if (grupo.matriz) matrices.push(grupo.matriz); });
    return matrices;
  };

  const fetchLinkedClients = async (clientId, matrixId) => {
    try {
      const token = localStorage.getItem('token');
      const matrixToSearch = matrixId > 0 ? matrixId : clientId;
      const response = await fetch(`${API_URL}/api/clientes/matriz/${matrixToSearch}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al obtener clientes vinculados');
      const data = await response.json();
      setLinkedClients(data.clientes.filter(c => c.id !== clientId));
    } catch {
      setLinkedClients([]);
    }
  };

  // filtrosActuales: por defecto usa el estado `filtros`, pero se le puede pasar
  // un override para evitar depender del closure (p. ej. al aplicar un filtro
  // recién cambiado, antes de que React re-renderice).
  const fetchPedidos = async (clientId, filtrosActuales = filtros) => {
    if (!clientId) { setPedidos([]); return; }
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filtrosActuales.npedido) params.append('npedido', filtrosActuales.npedido);
      if (filtrosActuales.fecha) params.append('fecha', filtrosActuales.fecha);
      if (filtrosActuales.estado) params.append('estado', filtrosActuales.estado);
      if (filtrosActuales.cliente) params.append('cliente', filtrosActuales.cliente);
      params.append('cliente_id', clientId);
      const response = await fetch(`${API_URL}/api/pedidos?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al obtener pedidos');
      const data = await response.json();
      setPedidos(data.pedidos || []);
    } catch {
      setPedidos([]);
    }
  };

  const fetchPedidosPendientes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pedidos/pendientes-aprobacion`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al obtener pedidos pendientes');
      const data = await response.json();
      setPedidosPendientes(data.pedidos || []);
    } catch {
      setPedidosPendientes([]);
    }
  };

  // Pedidos pendientes (estado 6) de la propia clínica hija. Reutiliza el GET
  // de /pedidos filtrando por su cliente_id y estado 6.
  const fetchPedidosPendientesHija = async (clientId = activeClient?.id) => {
    if (!clientId) { setPedidosPendientesHija([]); return; }
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('cliente_id', clientId);
      params.append('estado', '6');
      const response = await fetch(`${API_URL}/api/pedidos?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al obtener pedidos pendientes');
      const data = await response.json();
      setPedidosPendientesHija(data.pedidos || []);
    } catch {
      setPedidosPendientesHija([]);
    }
  };

  const fetchFacturasPendientes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/invoicing/pedidos/facturas-pendientes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al obtener facturas pendientes');
      const data = await response.json();
      setFacturasPendientes(data.pedidos || []);
    } catch {
      setFacturasPendientes([]);
    }
  };

  const viewInvoice = async (pedidoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/invoicing/pedidos/${pedidoId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        window.open(data.pdf_url, '_blank');
      } else {
        const data = await response.json();
        alert(data.message || 'Error al obtener la factura');
      }
    } catch { alert('Error al ver la factura'); }
  };

  const getInvoicePreview = async (pedidoId) => {
    try {
      setLoadingInvoicePreview(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/invoicing/pedidos/${pedidoId}/invoice-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setInvoicePreviewData(data);
        setShowInvoicePreviewModal(true);
      } else {
        const data = await response.json();
        alert(data.message || 'Error al obtener los datos de la factura');
      }
    } catch { alert('Error al procesar la factura'); }
    finally { setLoadingInvoicePreview(false); }
  };

  const confirmarFacturaYEnviarHolded = async () => {
    if (!invoicePreviewData) return;
    if (!window.confirm('¿Confirmar esta factura y enviarla a Holded? Esta acción no se puede deshacer.')) return;
    try {
      setConfirmingInvoice(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/invoicing/pedidos/${invoicePreviewData.pedido.id}/confirmar-factura`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        alert(`Factura confirmada y enviada a Holded correctamente.\nID en Holded: ${data.holdedId}`);
        setShowInvoicePreviewModal(false);
        setInvoicePreviewData(null);
        fetchFacturasPendientes();
      } else {
        const data = await response.json();
        alert(data.message || 'Error al confirmar y enviar la factura');
      }
    } catch { alert('Error al confirmar la factura'); }
    finally { setConfirmingInvoice(false); }
  };

  const handleFiltroChange = (key, value) => setFiltros(prev => ({ ...prev, [key]: value }));
  const aplicarFiltros = () => { if (activeClient) fetchPedidos(activeClient.id, filtros); };
  const limpiarFiltros = () => {
    const limpio = { npedido: '', fecha: '', estado: '', cliente: '' };
    setFiltros(limpio);
    if (activeClient) fetchPedidos(activeClient.id, limpio);
  };

  const getEstadoColor = (estado) => ({
    1: 'bg-blue-100 text-blue-800', 2: 'bg-yellow-100 text-yellow-800',
    3: 'bg-green-100 text-green-800', 4: 'bg-cyan-100 text-cyan-800',
    5: 'bg-green-200 text-green-900', 6: 'bg-orange-100 text-orange-800',
    7: 'bg-red-200 text-red-900',
  }[estado] || 'bg-gray-100 text-gray-800');

  const getEstadoTexto = (estado) => ({
    1: 'Abierto', 2: 'En Proceso', 3: 'Recibido', 4: 'Recibido Parcial',
    5: 'Cerrado', 6: 'Pendiente Aprobación', 7: 'Rechazado',
  }[estado] || 'Desconocido');

  const getEstadoIcon = (estado) => {
    const icons = { 1: <FileText size={16} />, 2: <RefreshCw size={16} />, 3: <CheckCircle size={16} />,
      4: <Clock size={16} />, 5: <Package size={16} />, 6: <AlertCircle size={16} />, 7: <XCircle size={16} /> };
    return icons[estado] || <FileText size={16} />;
  };

  const handleVerComentarios = async (pedido) => {
    setSelectedPedido(pedido);
    setShowCommentModal(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pedidos/${pedido.id}/comentarios`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) { const data = await response.json(); setComentarios(data.comentarios || []); }
    } catch { setComentarios([]); }
  };

  const addComentario = async () => {
    if (!nuevoComentario.trim() || !selectedPedido) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pedidos/${selectedPedido.id}/comentarios`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ comentario: nuevoComentario.trim() }),
      });
      if (response.ok) { handleVerComentarios(selectedPedido); setNuevoComentario(''); }
    } catch { console.error('Error al agregar comentario'); }
  };

  const handleEstadoChange = async (pedidoId, nuevoEstado) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pedidos/${pedidoId}/estado`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado: nuevoEstado }),
      });
      if (response.ok) {
        if (activeClient) fetchPedidos(activeClient.id);
        if (puedeAprobarPedidos()) fetchPedidosPendientes();
      }
    } catch { console.error('Error al cambiar estado'); }
  };

  const handleSeleccionarPedido = (pedidoId) => setSelectedPedidos(prev =>
    prev.includes(pedidoId) ? prev.filter(id => id !== pedidoId) : [...prev, pedidoId]);

  const handleSeleccionarTodos = () => setSelectedPedidos(
    selectedPedidos.length === pedidos.length ? [] : pedidos.map(p => p.id));

  const aplicarEstadoMasivo = async () => {
    if (!estadoMasivo || selectedPedidos.length === 0) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pedidos/estado-masivo`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pedidos: selectedPedidos, estado: parseInt(estadoMasivo) }),
      });
      if (response.ok) { setSelectedPedidos([]); setEstadoMasivo(''); if (activeClient) fetchPedidos(activeClient.id); }
    } catch { console.error('Error al aplicar estado masivo'); }
  };

  const handleAprobarPedido = async (pedido) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pedidos/${pedido.id}/aprobar`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ accion: 'aprobar' }),
      });
      if (response.ok) { fetchPedidosPendientes(); if (activeClient) fetchPedidos(activeClient.id); }
      else { const data = await response.json(); alert(data.message || 'Error al aprobar el pedido'); }
    } catch { alert('Error al aprobar el pedido'); }
  };

  const handleRechazarPedido = async () => {
    if (!pedidoAprobacion || !motivoRechazo.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pedidos/${pedidoAprobacion.id}/aprobar`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ accion: 'rechazar', motivo_rechazo: motivoRechazo.trim() }),
      });
      if (response.ok) { setShowApprovalModal(false); setPedidoAprobacion(null); setMotivoRechazo(''); fetchPedidosPendientes(); }
    } catch { console.error('Error al rechazar pedido'); }
  };

  const handleAprobarSeleccionados = async () => {
    if (selectedPedidosAprobacion.length === 0) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pedidos/aprobar-masivo`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pedidos: selectedPedidosAprobacion, accion: 'aprobar' }),
      });
      if (response.ok) { setSelectedPedidosAprobacion([]); fetchPedidosPendientes(); }
    } catch { console.error('Error al aprobar pedidos'); }
  };

  const handleDescargarPDF = async (pedidoId) => {
    try {
      setLoadingPdf(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pedidos/${pedidoId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al generar PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none'; a.href = url; a.download = `pedido_${pedidoId}.pdf`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch (e) {
      console.error('Error al descargar PDF', e);
      alert('No se pudo descargar el PDF del pedido. Inténtalo de nuevo o contacta con soporte.');
    }
    finally { setLoadingPdf(false); }
  };

  const handleDescargarCatalogo = async ({ proveedores = [], categorias = [] } = {}) => {
    if (!activeClient) return;
    try {
      setLoadingCatalogo(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (proveedores.length) params.append('proveedores', proveedores.join(','));
      if (categorias.length)  params.append('categorias', categorias.join(','));
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API_URL}/api/clientes/${activeClient.id}/catalogo${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al generar catálogo');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none'; a.href = url;
      a.download = `catalogo_${activeClient.razon_social.replace(/[^A-Za-z0-9]/g, '_')}.xlsx`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
      setShowCatalogoModal(false);
    } catch { console.error('Error al descargar catálogo'); }
    finally { setLoadingCatalogo(false); }
  };

  // Abre el modal de filtros del catálogo y carga los proveedores del cliente.
  // Las categorías son fijas (no se consultan).
  const abrirCatalogoModal = async () => {
    if (!activeClient) return;
    setSelProvIds([]); setSelCategorias([]);
    setShowCatalogoModal(true);
    try {
      const token = localStorage.getItem('token');
      const provResp = await fetch(`${API_URL}/api/clientes/${activeClient.id}/proveedores`, { headers: { Authorization: `Bearer ${token}` } });
      if (provResp.ok) { const d = await provResp.json(); setCatProviders(d.proveedores || []); }
    } catch { /* si falla, se puede descargar igual sin filtros */ }
  };

  // ── Gestión de albaranes ──────────────────────────────────────────────
  // La pestaña sólo se muestra si el cliente activo tiene gestion_albaranes.
  // La matriz propaga el flag a sus hijas (el backend ya lo fuerza en la fila
  // de cada hija), así que con leer el flag del cliente activo alcanza.
  const tieneGestionAlbaranes = () => !!activeClient && !!activeClient.gestion_albaranes;

  const fetchPedidosRecibidos = async (clienteId, filtros = filtroAlbaranes) => {
    if (!clienteId) return;
    try {
      setLoadingRecibidos(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ cliente_id: clienteId });
      if (filtros.fechaDesde) params.append('fecha_desde', filtros.fechaDesde);
      if (filtros.fechaHasta) params.append('fecha_hasta', filtros.fechaHasta);
      if (filtros.pedido) params.append('npedido', filtros.pedido);
      const response = await fetch(`${API_URL}/api/pedidos-recibidos?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error al obtener pedidos recibidos');
      const data = await response.json();
      setPedidosRecibidos(data.pedidos || []);
    } catch (e) {
      console.error('Error al obtener pedidos recibidos:', e);
      setPedidosRecibidos([]);
    } finally {
      setLoadingRecibidos(false);
    }
  };

  const handleDescargarZipAlbaranes = async () => {
    if (!activeClient) return;
    try {
      setDescargandoZip(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ cliente_id: activeClient.id });
      if (filtroAlbaranes.fechaDesde) params.append('fecha_desde', filtroAlbaranes.fechaDesde);
      if (filtroAlbaranes.fechaHasta) params.append('fecha_hasta', filtroAlbaranes.fechaHasta);
      if (filtroAlbaranes.pedido) params.append('npedido', filtroAlbaranes.pedido);
      const response = await fetch(`${API_URL}/api/albaranes-zip?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 404) {
        alert('No hay albaranes en el rango de fechas seleccionado');
        return;
      }
      if (!response.ok) throw new Error('Error al generar el zip');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none'; a.href = url;
      a.download = `albaranes_${activeClient.id}_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch (e) {
      console.error('Error al descargar zip de albaranes:', e);
      alert('Error al descargar el zip de albaranes');
    } finally {
      setDescargandoZip(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* ── Sidebar ────────────────────────────────────────────────────── */}
      {getCentrosAsociados().length > 0 && (
        <aside className="w-80 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Gestión de Clínicas</h1>
            <p className="text-sm text-gray-600">Gestiona pedidos y centros médicos</p>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Matrices */}
            {getTodasLasMatrices().length > 1 && (
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center mb-3">
                  <Building2 size={18} className="mr-2 text-gray-600" />
                  <h3 className="font-semibold text-gray-800">Clínicas Matrices</h3>
                </div>
                <div className="space-y-2">
                  {getTodasLasMatrices().map(matriz => (
                    <button
                      key={matriz.id}
                      onClick={() => handleMatrizSelect(matriz)}
                      className={`w-full text-left px-3 py-3 rounded-lg transition-all duration-200 border ${
                        clinicaMatrizActiva?.id === matriz.id ? 'shadow-sm' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      style={clinicaMatrizActiva?.id === matriz.id
                        ? { borderColor: theme.primary, backgroundColor: theme.primaryLight, color: theme.primaryDark }
                        : {}}
                    >
                      <div className="flex items-center">
                        <Building2 size={16} className="mr-2"
                          style={{ color: clinicaMatrizActiva?.id === matriz.id ? theme.primary : '#9ca3af' }} />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium truncate">{matriz.razon_social}</h4>
                          <p className="text-sm text-gray-500 truncate">{matriz.direccion}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Centros asociados */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <Building2 size={18} className="mr-2 text-gray-600" />
                  <h3 className="font-semibold text-gray-800">
                    {getTodasLasMatrices().length > 1 ? 'Centros Asociados' : 'Centros Médicos'}
                  </h3>
                </div>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {getCentrosAsociados().length}
                </span>
              </div>

              <div className="space-y-2">
                {getCentrosAsociados().map(cliente => (
                  <button
                    key={cliente.id}
                    onClick={() => handleClientSelect(cliente)}
                    className={`w-full text-left px-3 py-3 rounded-lg transition-all duration-200 border ${
                      activeClient?.id === cliente.id ? 'shadow-sm' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    style={activeClient?.id === cliente.id
                      ? { borderColor: theme.primary, backgroundColor: theme.primaryLight }
                      : {}}
                  >
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-3`}
                        style={{ backgroundColor: (cliente.clinica_matriz === 0 || cliente.clinica_matriz === null) ? theme.secondary : '#9ca3af' }} />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate">{cliente.razon_social}</h4>
                        <p className="text-sm text-gray-500 truncate">{cliente.direccion}</p>
                        <div className="flex items-center mt-1">
                          {(cliente.clinica_matriz === 0 || cliente.clinica_matriz === null) ? (
                            <span className="text-xs font-medium" style={{ color: theme.secondary }}>Matriz</span>
                          ) : (
                            <span className="text-xs text-gray-500">Centro asociado</span>
                          )}
                        </div>
                      </div>
                      {activeClient?.id === cliente.id && (
                        <div className="ml-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: theme.primary }} />
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>
      )}

      {/* ── Contenido Principal ─────────────────────────────────────────── */}
      <main className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          {activeClient ? (
            <div className="flex-1 flex flex-col">
              {/* Header del cliente */}
              <div className="bg-white shadow-sm border-b border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-full" style={bgPrimaryLight}>
                      <Building2 className="w-8 h-8" style={textPrimary} />
                    </div>
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">{activeClient.razon_social}</h1>
                      {activeClient.alias && <p className="text-gray-600">{activeClient.alias}</p>}
                      <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                        {activeClient.telefono1 && <div className="flex items-center"><Phone className="w-4 h-4 mr-1" />{activeClient.telefono1}</div>}
                        {activeClient.mail && <div className="flex items-center"><Mail className="w-4 h-4 mr-1" />{activeClient.mail}</div>}
                        {activeClient.cif && <div className="flex items-center"><CreditCard className="w-4 h-4 mr-1" />{activeClient.cif}</div>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <button
                      onClick={abrirCatalogoModal}
                      disabled={loadingCatalogo}
                      className="px-4 py-2 disabled:bg-gray-400 text-white rounded-lg transition-colors duration-200 flex items-center"
                      style={{ backgroundColor: loadingCatalogo ? undefined : theme.primaryDark }}
                    >
                      {loadingCatalogo ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      {loadingCatalogo ? 'Generando...' : 'Catálogo'}
                    </button>

                    <button
                      onClick={() => navigate('/nuevo-pedido', { state: { clienteId: activeClient.id } })}
                      className="px-4 py-2 text-white rounded-lg transition-colors duration-200 flex items-center hover:opacity-90"
                      style={bgPrimary}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Nuevo Pedido
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabs de navegación */}
              <div className="bg-white border-b border-gray-200">
                <div className="px-6">
                  <nav className="flex space-x-8">
                    {/* Tab: Mis Pedidos */}
                    <button
                      onClick={() => setVistaActiva('mis-pedidos')}
                      className="py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200"
                      style={vistaActiva === 'mis-pedidos'
                        ? { borderColor: theme.primary, color: theme.primary }
                        : { borderColor: 'transparent', color: '#6b7280' }}
                    >
                      <div className="flex items-center">
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Mis Pedidos
                        {pedidos.length > 0 && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            style={{ backgroundColor: theme.primaryLight, color: theme.primaryDark }}>
                            {pedidos.length}
                          </span>
                        )}
                      </div>
                    </button>

                    {/* Tab: Pendientes de Aprobación — solo si la matriz tiene el flag activo */}
                    {aprobacionActiva && (
                      <button
                        onClick={() => { setVistaActiva('pendientes-aprobacion'); fetchPedidosPendientes(); }}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                          vistaActiva === 'pendientes-aprobacion'
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center">
                          <UserCheck className="w-4 h-4 mr-2" />
                          Pendientes de Aprobación
                          {pedidosPendientes.length > 0 && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-400 text-yellow-900 font-bold">
                              {pedidosPendientes.length}
                            </span>
                          )}
                        </div>
                      </button>
                    )}

                    {/* Tab: Pedidos pendientes (clínica HIJA) — ve sus pedidos en
                        estado 6 y los edita; NO aprueba ni rechaza (eso es la matriz). */}
                    {hijaConPendientes && (
                      <button
                        onClick={() => { setVistaActiva('pendientes-hija'); fetchPedidosPendientesHija(); }}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                          vistaActiva === 'pendientes-hija'
                            ? 'border-amber-500 text-amber-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-2" />
                          Pedidos pendientes
                          {pedidosPendientesHija.length > 0 && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 font-bold">
                              {pedidosPendientesHija.length}
                            </span>
                          )}
                        </div>
                      </button>
                    )}

                    {/* Tab: Facturación */}
                    {puedeVerFacturacion() && (
                      <button
                        onClick={() => { setVistaActiva('facturacion'); fetchFacturasPendientes(); }}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                          vistaActiva === 'facturacion'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center">
                          <Receipt className="w-4 h-4 mr-2" />
                          Facturación
                          {facturasPendientes.length > 0 && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {facturasPendientes.length}
                            </span>
                          )}
                        </div>
                      </button>
                    )}

                    {/* La sección API Keys se movió al menú superior (/mis-api-keys). */}

                    {/* Tab: Albaranes — solo si el cliente tiene gestion_albaranes */}
                    {tieneGestionAlbaranes() && (
                      <button
                        onClick={() => { setVistaActiva('albaranes'); if (activeClient) fetchPedidosRecibidos(activeClient.id); }}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                          vistaActiva === 'albaranes'
                            ? 'border-teal-500 text-teal-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center">
                          <Truck className="w-4 h-4 mr-2" />
                          Albaranes
                        </div>
                      </button>
                    )}

                    {/* Tab: Incidencias — siempre disponible para la clínica */}
                    <button
                      onClick={() => { setVistaActiva('incidencias'); fetchIncContexto(); }}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                        vistaActiva === 'incidencias'
                          ? 'border-teal-500 text-teal-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        Incidencias
                      </div>
                    </button>
                  </nav>
                </div>
              </div>

              {/* ── Vista: Mis Pedidos ───────────────────────────────────── */}
              {vistaActiva === 'mis-pedidos' && (
                <>
                  {/* Filtros */}
                  <div className="bg-white border-b border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <Filter className="w-5 h-5 mr-2" style={textPrimary} />
                        Filtros
                      </h3>
                      {selectedPedidos.length > 0 && (
                        <div className="flex items-center space-x-3">
                          <select value={estadoMasivo} onChange={(e) => setEstadoMasivo(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent">
                            <option value="">Cambiar estado a...</option>
                            <option value="1">Abierto</option><option value="2">En Proceso</option>
                            <option value="3">Recibido</option><option value="4">Recibido Parcial</option>
                            <option value="5">Cerrado</option><option value="7">Rechazado</option>
                          </select>
                          <button onClick={aplicarEstadoMasivo} disabled={!estadoMasivo}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors duration-200 text-sm">
                            Aplicar ({selectedPedidos.length})
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {[
                        { key: 'npedido', label: 'Número de Pedido', type: 'text', placeholder: 'Buscar por número...' },
                        { key: 'fecha', label: 'Fecha', type: 'date', placeholder: '' },
                      ].map(f => (
                        <div key={f.key}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{f.label}</label>
                          <input type={f.type} value={filtros[f.key]} placeholder={f.placeholder}
                            onChange={(e) => handleFiltroChange(f.key, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                        </div>
                      ))}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                        <select value={filtros.estado}
                          onChange={(e) => {
                            const next = { ...filtros, estado: e.target.value };
                            setFiltros(next);
                            if (activeClient) fetchPedidos(activeClient.id, next);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent">
                          <option value="">En proceso (por defecto)</option>
                          <option value="1">Abierto</option><option value="2">En Proceso</option>
                          <option value="3">Recibido</option><option value="4">Recibido Parcial</option>
                          <option value="5">Cerrado</option><option value="6">Pendiente Aprobación</option>
                          <option value="7">Rechazado</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cliente</label>
                        <input type="text" value={filtros.cliente} placeholder="Buscar cliente..."
                          onChange={(e) => handleFiltroChange('cliente', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3 mt-4">
                      <button onClick={limpiarFiltros}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-sm">
                        Limpiar
                      </button>
                      <button onClick={aplicarFiltros}
                        className="px-4 py-2 text-white rounded-lg transition-colors duration-200 text-sm hover:opacity-90"
                        style={bgPrimary}>
                        Aplicar Filtros
                      </button>
                    </div>
                  </div>

                  {/* Tabla de pedidos */}
                  <div className="flex-1 overflow-auto">
                    {pedidos.length > 0 ? (
                      <div className="p-6">
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left">
                                    <input type="checkbox"
                                      checked={selectedPedidos.length === pedidos.length && pedidos.length > 0}
                                      onChange={handleSeleccionarTodos}
                                      className="h-4 w-4 border-gray-300 rounded"
                                      style={{ accentColor: theme.primary }} />
                                  </th>
                                  {['Pedido','Cliente','Fecha','Tipo','Estado','Total','Proveedor','Acciones'].map(h => (
                                    <th key={h} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${h === 'Acciones' ? 'text-right' : 'text-left'}`}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {pedidos.map((pedido) => (
                                  <tr key={pedido.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <input type="checkbox" checked={selectedPedidos.includes(pedido.id)}
                                        onChange={() => handleSeleccionarPedido(pedido.id)}
                                        className="h-4 w-4 border-gray-300 rounded"
                                        style={{ accentColor: theme.primary }} />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <Package className="w-4 h-4 text-gray-400 mr-2" />
                                        <span className="text-sm font-medium text-gray-900">#{pedido.id}</span>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-900">{pedido.razon_social}</div>
                                      {pedido.alias && <div className="text-sm text-gray-500">{pedido.alias}</div>}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center text-sm text-gray-900">
                                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                                        {new Date(pedido.fecha).toLocaleDateString('es-ES')}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {getTipoBadge(pedido.tipo, pedido.cliente, pedido.clinica_matriz)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {puedeModificarEstado(pedido) ? (
                                        <select value={pedido.estado}
                                          onChange={(e) => handleEstadoChange(pedido.id, parseInt(e.target.value))}
                                          className={`text-xs font-medium px-2.5 py-0.5 rounded-full border-0 ${getEstadoColor(pedido.estado)} cursor-pointer`}>
                                          <option value="1">Abierto</option><option value="2">En Proceso</option>
                                          <option value="3">Recibido</option><option value="4">Recibido Parcial</option>
                                          <option value="5">Cerrado</option><option value="7">Rechazado</option>
                                        </select>
                                      ) : (
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEstadoColor(pedido.estado)}`}>
                                          {getEstadoIcon(pedido.estado)}
                                          <span className="ml-1">{getEstadoTexto(pedido.estado)}</span>
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm font-medium text-gray-900">
                                        {pedido.itotaliva ? `€${parseFloat(pedido.itotaliva).toFixed(2)}` : 'N/A'}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-900">{pedido.proveedor_nombre || 'No asignado'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                      <div className="flex items-center justify-end space-x-2">
                                        <button onClick={() => handleVerComentarios(pedido)}
                                          className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors duration-200" title="Ver comentarios">
                                          <MessageCircle className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => handleDescargarPDF(pedido.id)} disabled={loadingPdf}
                                          className="text-purple-600 hover:text-purple-900 p-1 rounded transition-colors duration-200 disabled:opacity-50" title="Descargar PDF">
                                          <Download className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay pedidos</h3>
                          <p className="text-gray-500 mb-4">No se encontraron pedidos para los filtros aplicados.</p>
                          <button onClick={() => navigate('/nuevo-pedido', { state: { clienteId: activeClient.id } })}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white hover:opacity-90"
                            style={bgPrimary}>
                            <Plus className="w-4 h-4 mr-2" />Crear Primer Pedido
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ── Vista: Pendientes de Aprobación ─────────────────────── */}
              {vistaActiva === 'pendientes-aprobacion' && aprobacionActiva && (
                <>
                  <div className="bg-white border-b border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <UserCheck className="w-5 h-5 mr-2 text-amber-500" />
                        Pedidos Pendientes de Aprobación
                      </h3>
                      {selectedPedidosAprobacion.length > 0 && (
                        <button onClick={handleAprobarSeleccionados}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 text-sm flex items-center">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Aprobar Seleccionados ({selectedPedidosAprobacion.length})
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex-1 overflow-auto">
                    {pedidosPendientes.length > 0 ? (
                      <div className="p-6">
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left">
                                  <input type="checkbox"
                                    checked={selectedPedidosAprobacion.length === pedidosPendientes.length && pedidosPendientes.length > 0}
                                    onChange={() => setSelectedPedidosAprobacion(
                                      selectedPedidosAprobacion.length === pedidosPendientes.length ? [] : pedidosPendientes.map(p => p.id)
                                    )}
                                    className="h-4 w-4 border-gray-300 rounded"
                                    style={{ accentColor: theme.primary }} />
                                </th>
                                {['Pedido','Cliente','Fecha','Tipo','Total','Proveedor','Acciones'].map(h => (
                                  <th key={h} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${h === 'Acciones' ? 'text-right' : 'text-left'}`}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {pedidosPendientes.map((pedido) => (
                                <tr key={pedido.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <input type="checkbox" checked={selectedPedidosAprobacion.includes(pedido.id)}
                                      onChange={() => setSelectedPedidosAprobacion(prev =>
                                        prev.includes(pedido.id) ? prev.filter(id => id !== pedido.id) : [...prev, pedido.id])}
                                      className="h-4 w-4 border-gray-300 rounded"
                                      style={{ accentColor: theme.primary }} />
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center"><Package className="w-4 h-4 text-gray-400 mr-2" />
                                      <span className="text-sm font-medium text-gray-900">#{pedido.id}</span></div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{pedido.razon_social}</div>
                                    {pedido.alias && <div className="text-sm text-gray-500">{pedido.alias}</div>}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center text-sm text-gray-900">
                                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                                      {new Date(pedido.fecha).toLocaleDateString('es-ES')}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">{getTipoBadge(pedido.tipo, pedido.cliente, pedido.clinica_matriz)}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                      {pedido.itotaliva ? `€${parseFloat(pedido.itotaliva).toFixed(2)}` : 'N/A'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{pedido.proveedor_nombre || 'No asignado'}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex items-center justify-end space-x-2">
                                      <button onClick={() => handleAprobarPedido(pedido)}
                                        className="text-green-600 hover:text-green-900 px-3 py-1 rounded transition-colors duration-200 border border-green-600 hover:bg-green-50" title="Aprobar">
                                        <CheckCircle className="w-4 h-4" />
                                      </button>
                                      <button onClick={() => { setPedidoAprobacion(pedido); setShowApprovalModal(true); }}
                                        className="text-red-600 hover:text-red-900 px-3 py-1 rounded transition-colors duration-200 border border-red-600 hover:bg-red-50" title="Rechazar">
                                        <XCircle className="w-4 h-4" />
                                      </button>
                                      <button onClick={() => navigate(`/pedidos/detalles/${pedido.id}`)}
                                        className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors duration-200" title="Ver detalle">
                                        <Eye className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                          <UserCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay pedidos pendientes</h3>
                          <p className="text-gray-500">No hay pedidos esperando aprobación en este momento.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ── Vista: Pedidos pendientes (clínica HIJA) ─────────────── */}
              {vistaActiva === 'pendientes-hija' && hijaConPendientes && (
                <>
                  <div className="bg-white border-b border-gray-200 p-6">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <Clock className="w-5 h-5 mr-2 text-amber-500" />
                      Pedidos pendientes de aprobación
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Estos pedidos están esperando la aprobación de tu clínica matriz. Puedes
                      editarlos (añadir o quitar productos) mientras tanto; la aprobación o el
                      rechazo los gestiona la matriz.
                    </p>
                  </div>

                  <div className="flex-1 overflow-auto">
                    {pedidosPendientesHija.length > 0 ? (
                      <div className="p-6">
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                {['Pedido','Fecha','Tipo','Total','Proveedor','Acciones'].map(h => (
                                  <th key={h} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${h === 'Acciones' ? 'text-right' : 'text-left'}`}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {pedidosPendientesHija.map((pedido) => (
                                <tr key={pedido.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center"><Package className="w-4 h-4 text-gray-400 mr-2" />
                                      <span className="text-sm font-medium text-gray-900">{pedido.npedido || `#${pedido.id}`}</span></div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center text-sm text-gray-900">
                                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                                      {new Date(pedido.fecha).toLocaleDateString('es-ES')}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">{getTipoBadge(pedido.tipo, pedido.cliente, pedido.clinica_matriz)}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                      {pedido.itotaliva ? `€${parseFloat(pedido.itotaliva).toFixed(2)}` : 'N/A'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{pedido.proveedor_nombre || 'No asignado'}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button onClick={() => navigate(`/pedidos/detalles/${pedido.id}`)}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-white transition-colors duration-200 hover:opacity-90"
                                      style={bgPrimary} title="Editar pedido">
                                      <Edit className="w-4 h-4" /> Editar
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No tienes pedidos pendientes</h3>
                          <p className="text-gray-500">Cuando envíes un pedido que requiera aprobación, aparecerá aquí.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* La vista API Keys se movió a su propia página (/mis-api-keys), accesible desde el menú. */}

              {/* ── Vista: Facturación ───────────────────────────────────── */}
              {vistaActiva === 'facturacion' && puedeVerFacturacion() && (
                <>
                  <div className="bg-white border-b border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <Receipt className="w-5 h-5 mr-2 text-blue-600" />
                        Facturas Pendientes de Confirmación
                      </h3>
                      <div className="text-sm text-gray-600">
                        {facturasPendientes.length} factura{facturasPendientes.length !== 1 ? 's' : ''} pendiente{facturasPendientes.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 overflow-auto">
                    {facturasPendientes.length > 0 ? (
                      <div className="p-6">
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                {['Pedido','Cliente','Fecha','Tipo','Total Pedido','Proveedor','Factura','Acciones'].map(h => (
                                  <th key={h} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${h === 'Acciones' ? 'text-right' : 'text-left'}`}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {facturasPendientes.map((pedido) => (
                                <tr key={pedido.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center"><Package className="w-4 h-4 text-gray-400 mr-2" />
                                      <span className="text-sm font-medium text-gray-900">#{pedido.id}</span></div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{pedido.razon_social}</div>
                                    {pedido.alias && <div className="text-sm text-gray-500">{pedido.alias}</div>}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center text-sm text-gray-900">
                                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                                      {new Date(pedido.fecha).toLocaleDateString('es-ES')}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">{getTipoBadge(pedido.tipo, pedido.cliente, pedido.clinica_matriz)}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                      {pedido.itotaliva ? `€${parseFloat(pedido.itotaliva).toFixed(2)}` : 'N/A'}
                                    </div>
                                    <div className="text-xs text-gray-500">(Albarán)</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{pedido.proveedor_nombre || 'No asignado'}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      <FileText size={12} className="mr-1" />PDF disponible
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex items-center justify-end space-x-2">
                                      <button onClick={() => viewInvoice(pedido.id)}
                                        className="text-blue-600 hover:text-blue-900 px-3 py-1 rounded transition-colors duration-200 border border-blue-600 hover:bg-blue-50" title="Ver factura PDF">
                                        <Eye className="w-4 h-4" />
                                      </button>
                                      <button onClick={() => getInvoicePreview(pedido.id)} disabled={loadingInvoicePreview}
                                        className="text-green-600 hover:text-green-900 px-3 py-1 rounded transition-colors duration-200 border border-green-600 hover:bg-green-50 flex items-center disabled:opacity-50"
                                        title="Confirmar y enviar a Holded">
                                        {loadingInvoicePreview ? (
                                          <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                          <><FileCheck className="w-4 h-4 mr-1" /><span>Confirmar</span></>
                                        )}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                          <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay facturas pendientes</h3>
                          <p className="text-gray-500">No hay facturas esperando confirmación en este momento.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ── Vista: Albaranes ─────────────────────────────────────── */}
              {vistaActiva === 'albaranes' && tieneGestionAlbaranes() && (
                <>
                  {/* Filtros por rango de fecha + descarga zip */}
                  <div className="bg-white border-b border-gray-200 p-6">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
                      <Truck className="w-5 h-5 mr-2" style={{ color: theme.primary }} />
                      Pedidos recibidos
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Pedido</label>
                        <input
                          type="text"
                          value={filtroAlbaranes.pedido}
                          placeholder="Nº de pedido..."
                          onChange={(e) => setFiltroAlbaranes(prev => ({ ...prev, pedido: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter' && activeClient) fetchPedidosRecibidos(activeClient.id); }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Fecha desde</label>
                        <input
                          type="date"
                          value={filtroAlbaranes.fechaDesde}
                          max={filtroAlbaranes.fechaHasta || undefined}
                          onChange={(e) => setFiltroAlbaranes(prev => ({ ...prev, fechaDesde: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Fecha hasta</label>
                        <input
                          type="date"
                          value={filtroAlbaranes.fechaHasta}
                          min={filtroAlbaranes.fechaDesde || undefined}
                          onChange={(e) => setFiltroAlbaranes(prev => ({ ...prev, fechaHasta: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        />
                      </div>
                      {/* Aplicar filtros: botón secundario (borde con color de marca) */}
                      <button
                        onClick={() => activeClient && fetchPedidosRecibidos(activeClient.id)}
                        className="px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center justify-center bg-white border transition-all duration-200 hover:shadow-md active:scale-[0.98]"
                        style={{ borderColor: theme.primary, color: theme.primary }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme.primaryLight; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
                      >
                        <Filter className="w-4 h-4 mr-2" />
                        Aplicar filtros
                      </button>
                      {/* Descargar ZIP: botón primario sólido con elevación al hover */}
                      <button
                        onClick={handleDescargarZipAlbaranes}
                        disabled={descargandoZip}
                        className="px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center justify-center text-white shadow-sm transform transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-sm"
                        style={{ backgroundColor: descargandoZip ? theme.primaryDark : theme.primary }}
                        onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = theme.primaryDark; }}
                        onMouseLeave={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = theme.primary; }}
                      >
                        {descargandoZip ? (
                          <>
                            <span className="animate-spin h-4 w-4 mr-2 border-t-2 border-b-2 border-white rounded-full"></span>
                            Generando...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Descargar ZIP
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-auto">
                    {loadingRecibidos ? (
                      <div className="flex items-center justify-center h-64">
                        <span className="animate-spin h-8 w-8 border-t-2 border-b-2 border-teal-500 rounded-full"></span>
                      </div>
                    ) : pedidosRecibidos.length > 0 ? (
                      <div className="p-6">
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                {['Pedido','Fecha','Proveedor','Nº Albarán','Total','Albaranes (PDF)','Fecha estimada de entrega'].map(h => (
                                  <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {pedidosRecibidos.map((pedido) => (
                                <tr key={pedido.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center"><Package className="w-4 h-4 text-gray-400 mr-2" />
                                      <span className="text-sm font-medium text-gray-900">{pedido.npedido || `#${pedido.id}`}</span></div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center text-sm text-gray-900">
                                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                                      {pedido.fecha ? new Date(pedido.fecha).toLocaleDateString('es-ES') : '—'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{pedido.proveedor_nombre || 'No asignado'}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-700">{pedido.albaran || <span className="text-gray-400 italic">—</span>}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                      {pedido.itotaliva ? `€${parseFloat(pedido.itotaliva).toFixed(2)}` : 'N/A'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    {pedido.albaranes && pedido.albaranes.length > 0 ? (
                                      <div className="flex flex-col gap-1.5">
                                        {pedido.albaranes.map((a) => (
                                          <a
                                            key={a.id}
                                            href={`${API_URL}/api/albaranes/${a.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center text-sm text-teal-700 hover:text-teal-900 hover:underline"
                                          >
                                            <FileText size={14} className="mr-1" />
                                            {a.nombre}
                                          </a>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 italic text-sm">Sin albarán</span>
                                    )}
                                  </td>
                                  {/* Fecha estimada de entrega: una por albarán, alineada con la columna de PDFs */}
                                  <td className="px-6 py-4">
                                    {pedido.albaranes && pedido.albaranes.length > 0 ? (
                                      <div className="flex flex-col gap-1.5">
                                        {pedido.albaranes.map((a) => (
                                          <span key={a.id} className="text-sm text-gray-700 whitespace-nowrap">
                                            {a.fecha_entrega
                                              ? new Date(a.fecha_entrega).toLocaleDateString('es-ES')
                                              : <span className="text-gray-400 italic">—</span>}
                                          </span>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 italic text-sm">—</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                          <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay pedidos recibidos</h3>
                          <p className="text-gray-500">Ajusta el rango de fechas y vuelve a aplicar los filtros.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ── Vista: Incidencias ───────────────────────────────────── */}
              {vistaActiva === 'incidencias' && (
                <div className="p-4 sm:p-6">
                  {/* Sub-navegación: SOLO la clínica matriz ve el Panel de red */}
                  {esMatriz && (
                    <div className="flex gap-1 mb-5 border-b border-gray-200">
                      {[
                        { id:'incidencias', label:'Mis incidencias' },
                        { id:'panel',       label:'Panel de red' },
                      ].map(s => (
                        <button key={s.id} onClick={()=>{ setIncSeccion(s.id); if (s.id==='panel' && !incStats) fetchIncStats(); }}
                          className="px-4 py-2.5 text-sm font-semibold -mb-px border-b-2 transition-all"
                          style={incSeccion===s.id ? { borderColor: theme.primary, color: theme.primary } : { borderColor:'transparent', color:'#9ca3af' }}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {esMatriz && incSeccion === 'panel' ? (
                    <div className="space-y-5">
                      {/* Filtros de fecha + descargas */}
                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <div className="flex flex-wrap items-end gap-3">
                          <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Desde</label>
                            <input type="date" value={incRepDesde} onChange={e=>setIncRepDesde(e.target.value)}
                              className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-teal-500 focus:outline-none"/>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Hasta</label>
                            <input type="date" value={incRepHasta} onChange={e=>setIncRepHasta(e.target.value)}
                              className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-teal-500 focus:outline-none"/>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={()=>descargarReporteMias('xlsx')}
                              className="px-4 py-2 rounded-lg text-sm font-semibold border-2 border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 flex items-center gap-1.5">
                              <FileText className="w-4 h-4"/> Excel
                            </button>
                            <button onClick={()=>descargarReporteMias('pdf')}
                              className="px-4 py-2 rounded-lg text-sm font-semibold border-2 border-rose-200 text-rose-700 bg-rose-50 hover:bg-rose-100 flex items-center gap-1.5">
                              <Download className="w-4 h-4"/> PDF
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">El reporte incluye todas las incidencias de tu red de clínicas.</p>
                      </div>

                      {incStatsLoading ? (
                        <div className="py-16 text-center text-gray-400">Cargando estadísticas…</div>
                      ) : !incStats ? (
                        <div className="py-16 text-center text-gray-400">No hay datos para mostrar.</div>
                      ) : (
                        <>
                          {/* KPIs */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                              { label:'Creadas',    value: Number(incStats.kpis.total_incidencias), color:'#0ea5e9' },
                              { label:'Resueltas',  value: Number(incStats.kpis.resueltas), color:'#10b981' },
                              { label:'Pendientes', value: Number(incStats.kpis.abiertas) + Number(incStats.kpis.en_proceso), color:'#f59e0b' },
                              { label:'Horas',      value: Number(incStats.kpis.total_horas).toFixed(1), color:'#6366f1' },
                            ].map(k => (
                              <div key={k.label} className="bg-white rounded-xl border border-gray-200 p-4">
                                <div className="text-2xl font-bold tabular-nums" style={{ color:k.color }}>{k.value}</div>
                                <div className="text-xs text-gray-400 mt-1">{k.label}</div>
                              </div>
                            ))}
                          </div>

                          {/* Distribuciones */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {barBlock('Por estado', incStats.distribucion_estado.map(e=>({ label:capit(e.estado), value:Number(e.total), color:EST_COL[e.estado]||'#9ca3af' })))}
                            {barBlock('Por prioridad', incStats.distribucion_prioridad.map(p=>({ label:capit(p.prioridad), value:Number(p.total), color:INC_PRIO_COL[p.prioridad]||'#9ca3af' })))}
                          </div>
                          {barBlock('Por tipo', incStats.distribucion_tipo.map(t=>({ label:t.tipo_incidencia, value:Number(t.total), color:theme.primary })))}

                          {/* Evolución mensual */}
                          <div className="bg-white rounded-xl border border-gray-200 p-5">
                            <h4 className="text-sm font-bold text-gray-700 mb-3">Evolución (últimos 12 meses)</h4>
                            {incStats.evolucion_mensual.length === 0 ? <p className="text-xs text-gray-400">Sin datos.</p> : (
                              <div className="flex items-end gap-2 h-32">
                                {(() => {
                                  const max = Math.max(1, ...incStats.evolucion_mensual.map(m=>Number(m.total)));
                                  return incStats.evolucion_mensual.map((m,i)=>(
                                    <div key={i} className="flex-1 flex flex-col items-center gap-1 justify-end">
                                      <div className="w-full rounded-t" title={`${m.mes}: ${m.total}`}
                                        style={{ height:`${(Number(m.total)/max)*100}%`, minHeight:'3px', backgroundColor:theme.primary }}/>
                                      <span className="text-[9px] text-gray-400 whitespace-nowrap">{m.mes}</span>
                                    </div>
                                  ));
                                })()}
                              </div>
                            )}
                          </div>

                          {/* Por clínica */}
                          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <div className="px-5 py-3 border-b border-gray-100"><h4 className="text-sm font-bold text-gray-700">Por clínica</h4></div>
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-sm">
                                <thead className="bg-gray-50"><tr className="text-left text-gray-500">
                                  <th className="px-4 py-2 font-semibold">Clínica</th>
                                  <th className="px-4 py-2 font-semibold text-right">Total</th>
                                  <th className="px-4 py-2 font-semibold text-right">Pendientes</th>
                                  <th className="px-4 py-2 font-semibold text-right">Horas</th>
                                </tr></thead>
                                <tbody className="divide-y divide-gray-100">
                                  {incStats.por_clinica.length === 0 ? (
                                    <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">Sin incidencias en tu red.</td></tr>
                                  ) : incStats.por_clinica.map((c,i)=>(
                                    <tr key={i} className="hover:bg-gray-50">
                                      <td className="px-4 py-2 text-gray-700">{c.razon_social}</td>
                                      <td className="px-4 py-2 text-right text-gray-700 tabular-nums">{c.total}</td>
                                      <td className="px-4 py-2 text-right text-amber-600 tabular-nums">{c.pendientes}</td>
                                      <td className="px-4 py-2 text-right text-gray-500 tabular-nums">{Number(c.horas).toFixed(1)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                  <>
                  {/* Formulario de registro */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-1 flex items-center">
                      <AlertCircle className="w-5 h-5 mr-2 text-teal-600" /> Registrar una incidencia
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">Contanos qué pasó con un pedido o producto. Nuestro equipo la revisará.</p>
                    {incMsg && (
                      <div className={`mb-3 text-sm px-3 py-2 rounded-lg ${incMsg.includes('correctamente') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{incMsg}</div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-bold text-gray-700 mb-1">Título *</label>
                        <input type="text" value={incForm.nombre}
                          onChange={(e) => setIncForm(f => ({ ...f, nombre: e.target.value }))}
                          placeholder="Ej. Faltan unidades en el pedido 58699"
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Prioridad</label>
                        <select value={incForm.prioridad}
                          onChange={(e) => setIncForm(f => ({ ...f, prioridad: e.target.value }))}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none text-sm bg-white">
                          <option value="baja">Baja</option>
                          <option value="media">Media</option>
                          <option value="alta">Alta</option>
                          <option value="critica">Crítica</option>
                        </select>
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-sm font-bold text-gray-700 mb-1">Descripción</label>
                        <textarea value={incForm.descripcion}
                          onChange={(e) => setIncForm(f => ({ ...f, descripcion: e.target.value }))}
                          rows={3} placeholder="Detalle de la incidencia..."
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none text-sm" />
                      </div>

                      {/* Imágenes adjuntas: una foto explica la incidencia mucho
                          mejor que un párrafo (caja rota, referencia equivocada…). */}
                      <div className="md:col-span-3">
                        <label className="block text-sm font-bold text-gray-700 mb-1">
                          Imágenes <span className="font-normal text-gray-400">· opcional, hasta {INC_MAX_ARCHIVOS} · máx. 15MB cada una</span>
                        </label>
                        <label
                          className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-gray-300 text-sm text-gray-500 w-fit"
                          style={incArchivos.length ? { borderColor: theme.primary, color: theme.primary } : undefined}>
                          <FileText className="w-4 h-4" />
                          {incArchivos.length ? `${incArchivos.length} imagen(es) seleccionada(s) — añadir más` : 'Adjuntar imágenes'}
                          <input
                            type="file"
                            multiple
                            accept="image/png,image/jpeg,image/gif,image/webp"
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files?.length) anadirIncArchivos(e.target.files);
                              e.target.value = ''; // permite volver a elegir el mismo fichero
                            }} />
                        </label>

                        {incArchivos.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {incPreviews.map(({ file: f, url }, i) => (
                              <div key={`${f.name}-${f.size}-${i}`} className="relative">
                                <img
                                  src={url}
                                  alt={f.name}
                                  title={f.name}
                                  className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                                <button
                                  type="button"
                                  onClick={() => quitarIncArchivo(i)}
                                  title="Quitar"
                                  className="absolute -top-1.5 -right-1.5 bg-white border border-gray-200 rounded-full p-0.5 shadow-sm hover:bg-gray-50">
                                  <X className="w-3 h-3 text-gray-500" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-end mt-4">
                      <button onClick={crearIncidencia} disabled={incSaving || !incForm.nombre.trim()}
                        style={bgPrimary}
                        className="px-4 py-2 rounded-lg text-white font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center">
                        <Plus className="w-4 h-4 mr-2" />{incSaving ? 'Registrando…' : 'Registrar incidencia'}
                      </button>
                    </div>
                  </div>

                  {/* Listado / Tablero de las incidencias de la red */}
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <h3 className="text-base font-bold text-gray-800">
                      {activeClient
                        ? (esClienteHija ? `Incidencias · ${activeClient.razon_social}` : 'Incidencias · Todas las sucursales')
                        : 'Incidencias'}
                    </h3>
                    <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden bg-white">
                      {[
                        { id:'tablero', label:'Tablero', icon:<Package className="w-3.5 h-3.5"/> },
                        { id:'tabla',   label:'Tabla',   icon:<FileText className="w-3.5 h-3.5"/> },
                      ].map(v => (
                        <button key={v.id} onClick={()=>setIncVista(v.id)}
                          className="px-3 py-1.5 text-sm font-semibold flex items-center gap-1.5 transition-all"
                          style={incVista===v.id ? { backgroundColor: theme.primary, color:'#fff' } : { color:'#6b7280' }}>
                          {v.icon}{v.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {incLoading ? (
                    <div className="py-10 text-center text-gray-400">Cargando…</div>
                  ) : incVista === 'tablero' ? (
                    <div>
                      <style>{`
                        @keyframes incCliCardIn { from { opacity:0; transform: translateY(7px) scale(.98) } to { opacity:1; transform:none } }
                        .inccli-card { animation: incCliCardIn .34s cubic-bezier(.22,1,.36,1) both; transition: transform .16s ease, box-shadow .16s ease; cursor: grab; }
                        .inccli-card:hover { transform: translateY(-3px); box-shadow: 0 10px 24px rgba(15,40,60,.10); }
                        .inccli-card:active { cursor: grabbing; }
                        .inccli-card.dragging { opacity:.35; }
                        .inccli-col { transition: background-color .18s ease, box-shadow .18s ease, border-color .18s ease; }
                        @media (prefers-reduced-motion: reduce) { .inccli-card { animation: none } .inccli-card:hover { transform:none } }
                      `}</style>
                      <div className="flex gap-3 overflow-x-auto pb-2">
                        {INC_COLS.map(col => {
                          const items = incidencias.filter(i => (i.estado || 'abierta') === col.key);
                          const activa = incDropCol === col.key;
                          return (
                            <div key={col.key}
                              onDragOver={(e)=>{ e.preventDefault(); if (incDropCol !== col.key) setIncDropCol(col.key); }}
                              onDragLeave={(e)=>{ if (e.currentTarget === e.target) setIncDropCol(null); }}
                              onDrop={(e)=>{
                                e.preventDefault();
                                const id = Number(e.dataTransfer.getData('text/plain'));
                                const inc = incidencias.find(x=>x.id===id);
                                if (inc) cambiarEstadoIncCli(inc, col.key);
                                setIncDropCol(null); setIncDragId(null);
                              }}
                              className="inccli-col flex-shrink-0 w-64 rounded-2xl border p-2"
                              style={{ backgroundColor: activa ? `${col.color}12` : '#f8fafc',
                                       borderColor: activa ? col.color : '#eef2f6',
                                       boxShadow: activa ? `inset 0 0 0 2px ${col.color}66` : 'none' }}>
                              <div className="flex items-center justify-between px-2 py-2">
                                <div className="flex items-center gap-2">
                                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }}/>
                                  <span className="text-xs font-bold uppercase tracking-wider text-gray-500">{col.label}</span>
                                </div>
                                <span className="text-[11px] font-bold text-gray-400 bg-white rounded-full px-2 py-0.5 border border-gray-100">{items.length}</span>
                              </div>
                              <div className="space-y-2 min-h-[110px] px-1 pb-1">
                                {items.length === 0 ? (
                                  <div className="text-center text-[11px] text-gray-300 py-9 border-2 border-dashed border-gray-200/70 rounded-xl">Arrastra aquí</div>
                                ) : items.map(inc => (
                                  <div key={inc.id} draggable
                                    title="Click para ver el detalle · arrastrá para cambiar de estado"
                                    onDragStart={(e)=>{ setIncDragId(inc.id); e.dataTransfer.setData('text/plain', String(inc.id)); e.dataTransfer.effectAllowed='move'; }}
                                    onDragEnd={()=>{ setIncDragId(null); setIncDropCol(null); }}
                                    onPointerDown={(e)=>{ incPressPos.current = { x: e.clientX, y: e.clientY }; }}
                                    onPointerUp={(e)=>{
                                      // Solo es un click si el puntero apenas se movió. Con un onClick
                                      // normal, soltar un arrastre sobre la tarjeta de origen abriría
                                      // la ficha; el umbral tolera el temblor de ratón sin confundirse.
                                      const p = incPressPos.current;
                                      incPressPos.current = null;
                                      if (!p || incDragId !== null) return;
                                      const dx = e.clientX - p.x, dy = e.clientY - p.y;
                                      if (dx*dx + dy*dy <= INC_UMBRAL_CLICK*INC_UMBRAL_CLICK) setIncDetalle(inc);
                                    }}
                                    className={`inccli-card bg-white rounded-xl border border-gray-100 shadow-sm p-3 ${incDragId===inc.id?'dragging':''}`}
                                    style={{ borderLeft:`3px solid ${INC_PRIO_COL[inc.prioridad]||'#cbd5e1'}` }}>
                                    <div className="text-xs font-semibold text-gray-800 leading-snug line-clamp-2">{inc.nombre}</div>
                                    {inc.tipo_incidencia && (
                                      <div className="mt-1.5"><span className="inline-block text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ backgroundColor:`${theme.primary}14`, color:theme.primary }}>{inc.tipo_incidencia}</span></div>
                                    )}
                                    <div className="flex items-center justify-between mt-2.5">
                                      {prioridadIncBadge(inc.prioridad)}
                                      <div className="flex items-center gap-1.5">
                                        {/* adjuntos_count ya viene en el listado: señalar que hay fotos
                                            sin pagar una petición extra por tarjeta. */}
                                        {inc.adjuntos_count > 0 && (
                                          <span className="flex items-center gap-0.5 text-[10px] text-gray-400" title={`${inc.adjuntos_count} adjunto(s)`}>
                                            <Paperclip size={10}/>{inc.adjuntos_count}
                                          </span>
                                        )}
                                        <span className="text-[10px] font-mono text-gray-300">#{inc.id}</span>
                                      </div>
                                    </div>
                                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-50 text-[10px] text-gray-400">
                                      <div className="flex items-center gap-1 min-w-0"><Building2 size={10} className="flex-shrink-0"/><span className="truncate">{inc.cliente_nombre}</span></div>
                                      <div className="flex items-center gap-1 flex-shrink-0"><Calendar size={10}/><span>{inc.fecha ? new Date(inc.fecha).toLocaleDateString('es-ES',{day:'2-digit',month:'short'}) : '—'}</span></div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : incidencias.length === 0 ? (
                    <div className="py-10 text-center text-gray-400">Todavía no registraste ninguna incidencia.</div>
                  ) : (
                    <div className="overflow-x-auto bg-white rounded-xl border border-gray-200">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr className="text-left text-gray-500">
                            <th className="px-4 py-3 font-semibold">Título</th>
                            <th className="px-4 py-3 font-semibold">Prioridad</th>
                            <th className="px-4 py-3 font-semibold">Estado</th>
                            <th className="px-4 py-3 font-semibold">Fecha</th>
                            <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {incidencias.map((i) => (
                            <tr key={i.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-800">{i.nombre}</div>
                                {i.descripcion && <div className="text-xs text-gray-400 truncate max-w-xs">{i.descripcion}</div>}
                              </td>
                              <td className="px-4 py-3">{prioridadIncBadge(i.prioridad)}</td>
                              <td className="px-4 py-3">{estadoIncBadge(i.estado)}</td>
                              <td className="px-4 py-3 text-gray-600">{i.fecha ? new Date(i.fecha).toLocaleDateString('es-ES') : '—'}</td>
                              <td className="px-4 py-3 text-right">
                                <button onClick={() => setIncDetalle(i)} title="Ver detalle"
                                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors">
                                  <Eye size={14}/>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  </>
                  )}
                </div>
              )}

              {/* ── Modal: Detalle de incidencia ─────────────────────────────
                  OJO con detalleUrl: la ruta de admin (/api/incidencias/:id) es
                  soloAdmin y devuelve 403 acá. La del cliente es /mias/:id, que
                  el backend acota a la red de su clínica. */}
              <IncidenciaDetalleModal
                incidencia={incDetalle}
                detalleUrl={(id) => `${API_URL}/api/incidencias/mias/${id}`}
                onClose={() => setIncDetalle(null)}
              />

              {/* ── Modal: Filtros del catálogo ──────────────────────────── */}
              {showCatalogoModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                    <div className="text-white p-4 border-b flex-shrink-0" style={bgPrimary}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                            <Download size={20} className="text-white" />
                          </div>
                          <div>
                            <h2 className="text-xl font-bold text-white">Descargar catálogo</h2>
                            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                              Elegí proveedores y categorías, o dejá vacío para incluir todo
                            </p>
                          </div>
                        </div>
                        <button onClick={() => setShowCatalogoModal(false)}
                          className="p-2 rounded-lg transition-all duration-200 hover:bg-red-500 text-white">
                          <X size={20} />
                        </button>
                      </div>
                    </div>
                    <div className="p-4 overflow-y-auto flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-semibold text-gray-800">Proveedores</h5>
                          <button type="button" className="text-xs text-gray-500 hover:text-gray-700"
                            onClick={() => setSelProvIds([])}>Todos</button>
                        </div>
                        <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100">
                          {catProviders.length === 0 && (
                            <p className="text-xs text-gray-400 p-3">Cargando proveedores…</p>
                          )}
                          {catProviders.map(pv => (
                            <label key={pv.id} className="flex items-center gap-2 p-2 text-sm hover:bg-gray-50 cursor-pointer">
                              <input type="checkbox" className="accent-teal-500"
                                checked={selProvIds.includes(pv.id)}
                                onChange={(e) => setSelProvIds(prev => e.target.checked ? [...prev, pv.id] : prev.filter(id => id !== pv.id))} />
                              <span className="truncate">{pv.nombre}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-semibold text-gray-800">Categorías</h5>
                          <button type="button" className="text-xs text-gray-500 hover:text-gray-700"
                            onClick={() => setSelCategorias([])}>Todas</button>
                        </div>
                        <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100">
                          {[
                            { key: 'cirugia',     label: 'Cirugía' },
                            { key: 'medicina',    label: 'Medicina' },
                            { key: 'oficina',     label: 'Oficina' },
                            { key: 'limpieza',    label: 'Limpieza' },
                            { key: 'uniformes',   label: 'Uniformes' },
                            { key: 'informatica', label: 'Informática' },
                          ].map(cat => (
                            <label key={cat.key} className="flex items-center gap-2 p-2 text-sm hover:bg-gray-50 cursor-pointer">
                              <input type="checkbox" className="accent-teal-500"
                                checked={selCategorias.includes(cat.key)}
                                onChange={(e) => setSelCategorias(prev => e.target.checked ? [...prev, cat.key] : prev.filter(k => k !== cat.key))} />
                              <span className="truncate">{cat.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="p-4 border-t flex items-center justify-between flex-shrink-0">
                      <span className="text-xs text-gray-500">
                        {selProvIds.length === 0 ? 'Todos los proveedores' : `${selProvIds.length} proveedor(es)`}
                        {' · '}
                        {selCategorias.length === 0 ? 'todas las categorías' : `${selCategorias.length} categoría(s)`}
                      </span>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setShowCatalogoModal(false)}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">
                          Cancelar
                        </button>
                        <button onClick={() => handleDescargarCatalogo({ proveedores: selProvIds, categorias: selCategorias })}
                          disabled={loadingCatalogo}
                          className="px-4 py-2 text-white rounded-lg text-sm flex items-center disabled:opacity-60"
                          style={bgPrimary}>
                          {loadingCatalogo
                            ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            : <Download className="w-4 h-4 mr-2" />}
                          Descargar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Modal: Comentarios ───────────────────────────────────── */}
              {showCommentModal && selectedPedido && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
                    <div className="text-white p-4 border-b" style={bgPrimary}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <MessageCircle size={20} className="mr-2" />
                          <div>
                            <h2 className="text-lg font-bold">Comentarios del Pedido</h2>
                            <p className="text-sm opacity-80">Pedido #{selectedPedido.id}</p>
                          </div>
                        </div>
                        <button onClick={() => setShowCommentModal(false)}
                          className="p-2 rounded-lg transition-all duration-200 hover:bg-red-500">
                          <X size={20} />
                        </button>
                      </div>
                    </div>
                    <div className="p-4 flex flex-col h-96">
                      <div className="flex-1 overflow-y-auto mb-4 space-y-3">
                        {comentarios.length > 0 ? comentarios.map((comentario, index) => (
                          <div key={index}
                            className={`p-3 rounded-lg max-w-[80%] ${comentario.usuario_id === user?.id ? 'ml-auto' : 'bg-gray-100'}`}
                            style={comentario.usuario_id === user?.id ? { backgroundColor: theme.primaryLight } : {}}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{comentario.username}</span>
                              <span className="text-xs text-gray-500">{new Date(comentario.fecha).toLocaleString('es-ES')}</span>
                            </div>
                            <p className="text-sm">{comentario.texto}</p>
                          </div>
                        )) : (
                          <div className="text-center text-gray-500 py-8">No hay comentarios para este pedido</div>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <textarea value={nuevoComentario} onChange={(e) => setNuevoComentario(e.target.value)}
                          placeholder="Escribe un comentario..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:border-transparent"
                          rows="2" />
                        <button onClick={addComentario} disabled={!nuevoComentario.trim()}
                          className="px-4 py-2 text-white rounded-lg transition-colors duration-200 disabled:bg-gray-400 hover:opacity-90"
                          style={{ backgroundColor: nuevoComentario.trim() ? theme.primary : undefined }}>
                          Enviar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Modal: Rechazo ────────────────────────────────────────── */}
              {showApprovalModal && pedidoAprobacion && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
                    <div className="bg-red-600 text-white p-4 border-b">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <XCircle size={20} className="mr-2" />
                          <div>
                            <h2 className="text-lg font-bold">Rechazar Pedido</h2>
                            <p className="text-sm opacity-80">Pedido #{pedidoAprobacion.id}</p>
                          </div>
                        </div>
                        <button onClick={() => { setShowApprovalModal(false); setPedidoAprobacion(null); setMotivoRechazo(''); }}
                          className="p-2 rounded-lg transition-all duration-200 hover:bg-red-700"><X size={20} /></button>
                      </div>
                    </div>
                    <div className="p-6">
                      <p className="text-gray-700 mb-2"><strong>Clínica:</strong> {pedidoAprobacion.razon_social}</p>
                      <p className="text-gray-700 mb-2"><strong>Total:</strong> {pedidoAprobacion.itotaliva ? `€${parseFloat(pedidoAprobacion.itotaliva).toFixed(2)}` : 'N/A'}</p>
                      <p className="text-gray-700 mb-4"><strong>Proveedor:</strong> {pedidoAprobacion.proveedor_nombre || 'No asignado'}</p>
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Motivo del rechazo <span className="text-red-500">*</span></label>
                        <textarea value={motivoRechazo} onChange={(e) => setMotivoRechazo(e.target.value)}
                          placeholder="Explique por qué se rechaza este pedido..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent" rows="4" />
                      </div>
                      <div className="flex space-x-3">
                        <button onClick={() => { setShowApprovalModal(false); setPedidoAprobacion(null); setMotivoRechazo(''); }}
                          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                          Cancelar
                        </button>
                        <button onClick={handleRechazarPedido} disabled={!motivoRechazo.trim()}
                          className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg transition-colors duration-200 flex items-center justify-center">
                          <XCircle size={18} className="mr-2" />Rechazar Pedido
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Modal: Preview de Factura ────────────────────────────── */}
              {showInvoicePreviewModal && invoicePreviewData && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ maxHeight: '85vh' }}>
                    <div className="px-6 py-4 flex justify-between items-center rounded-t-xl flex-shrink-0 shadow-md" style={bgPrimary}>
                      <div className="flex items-center text-white">
                        <Receipt className="w-6 h-6 mr-3" />
                        <div>
                          <h2 className="text-xl font-bold">Preview de Factura</h2>
                          <p className="text-sm opacity-90">
                            Factura #{invoicePreviewData.factura.numeroFactura} - {invoicePreviewData.pedido.proveedor}
                          </p>
                        </div>
                      </div>
                      <button onClick={() => { setShowInvoicePreviewModal(false); setInvoicePreviewData(null); }}
                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all duration-200">
                        <X size={24} />
                      </button>
                    </div>

                    <div className="overflow-y-auto flex-1 p-6 space-y-4 bg-gray-50">
                      {/* Datos de la Factura */}
                      <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-gray-200">
                        <div className="flex items-center mb-3 pb-3 border-b border-gray-200">
                          <FileText className="w-5 h-5 mr-2" style={textPrimary} />
                          <h3 className="font-bold text-gray-800">Datos de la Factura</h3>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          {[
                            ['Número de Factura', invoicePreviewData.factura.numeroFactura],
                            ['Fecha de Emisión', invoicePreviewData.factura.fechaEmision || invoicePreviewData.pedido.fecha],
                            ['Vencimiento', invoicePreviewData.factura.fechaVencimiento],
                            ['Forma de Pago', invoicePreviewData.factura.formaPago],
                            ['Proveedor', invoicePreviewData.pedido.proveedor],
                            ['Pedido Nº', invoicePreviewData.pedido.npedido],
                          ].map(([label, val]) => (
                            <div key={label}>
                              <p className="text-xs text-gray-500 mb-1">{label}</p>
                              <p className="font-semibold text-gray-900">{val || 'N/A'}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Items */}
                      {invoicePreviewData.factura.items?.length > 0 && (
                        <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-gray-200">
                          <div className="flex items-center mb-3 pb-3 border-b border-gray-200">
                            <Package className="w-5 h-5 mr-2" style={textPrimary} />
                            <h3 className="font-bold text-gray-800">Líneas de Factura</h3>
                            <span className="ml-auto text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                              {invoicePreviewData.factura.items.length} producto{invoicePreviewData.factura.items.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  {['Código','Descripción','Cant.','Precio','IVA%','Total'].map(h => (
                                    <th key={h} className={`px-3 py-3 text-xs font-semibold text-gray-600 uppercase ${['Cant.','Precio','IVA%','Total'].includes(h) ? 'text-right' : 'text-left'}`}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {invoicePreviewData.factura.items.map((item, idx) => (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-3 py-3 text-xs font-mono text-gray-900">{item.codigo}</td>
                                    <td className="px-3 py-3 text-xs text-gray-700">{item.descripcion}</td>
                                    <td className="px-3 py-3 text-xs text-right text-gray-900">{item.cantidad}</td>
                                    <td className="px-3 py-3 text-xs text-right text-gray-900">€{item.precio.toFixed(2)}</td>
                                    <td className="px-3 py-3 text-xs text-right">
                                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${item.iva === 4 ? 'bg-blue-100 text-blue-800' : item.iva === 10 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                                        {item.iva}%
                                      </span>
                                    </td>
                                    <td className="px-3 py-3 text-xs text-right font-semibold text-gray-900">€{item.total.toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Totales */}
                      <div className="bg-white p-5 rounded-lg shadow-md border-2" style={borderPrimary}>
                        <div className="flex items-center mb-4 pb-3 border-b border-gray-200">
                          <Receipt className="w-5 h-5 mr-2" style={textPrimary} />
                          <h3 className="font-bold text-gray-800 text-lg">Resumen de Totales</h3>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between text-base bg-gray-50 px-4 py-3 rounded-lg">
                            <span className="font-medium text-gray-700">Base Imponible:</span>
                            <span className="font-semibold text-gray-900">€{(invoicePreviewData.factura.baseImponible || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-base bg-gray-50 px-4 py-3 rounded-lg">
                            <span className="font-medium text-gray-700">IVA Total:</span>
                            <span className="font-semibold text-gray-900">€{(invoicePreviewData.factura.importeIVA || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-xl px-5 py-4 rounded-lg shadow-sm" style={{ backgroundColor: theme.primaryLight, border: `2px solid ${theme.primary}` }}>
                            <span className="font-bold" style={{ color: theme.primaryDark }}>TOTAL FACTURA:</span>
                            <span className="font-bold" style={{ color: theme.primaryDark }}>€{(invoicePreviewData.factura.totalConIVA || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
                        <div className="flex">
                          <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-yellow-800">
                            <strong>⚠️ Importante:</strong> Al confirmar, esta factura se enviará automáticamente a Holded y se marcará como confirmada. Esta acción no se puede deshacer.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-100 px-6 py-4 border-t border-gray-200 flex justify-end gap-3 rounded-b-xl flex-shrink-0">
                      <button onClick={() => { setShowInvoicePreviewModal(false); setInvoicePreviewData(null); }}
                        className="px-5 py-2.5 text-sm font-medium border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                        Cancelar
                      </button>
                      <button onClick={confirmarFacturaYEnviarHolded} disabled={confirmingInvoice}
                        className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 shadow-md hover:opacity-90 transition-all"
                        style={{ backgroundColor: confirmingInvoice ? undefined : theme.primary }}>
                        {confirmingInvoice ? (
                          <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Enviando...</>
                        ) : (
                          <><CheckCircle size={18} />Confirmar y Enviar a Holded</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Estado inicial sin cliente seleccionado */
            <div className="flex flex-col items-center justify-center h-full py-12">
              <div className="text-center max-w-2xl">
                <div className="bg-white rounded-2xl shadow-lg p-12 border border-gray-100">
                  <div className="mb-8">
                    <div className="p-6 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center" style={bgPrimaryLight}>
                      {loading ? (
                        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-opacity-75" style={{ borderColor: theme.primary }} />
                      ) : (
                        <Building2 className="w-12 h-12" style={textPrimary} />
                      )}
                    </div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-4">
                      {loading ? 'Cargando...' : 'Centros Médicos'}
                    </h2>
                    <p className="text-gray-600 text-lg leading-relaxed">
                      {loading ? 'Estamos cargando tu información de centros médicos...' : 'Selecciona un centro médico de la lista de la izquierda para comenzar.'}
                    </p>
                    {!loading && puedeAprobarPedidos() && pedidosPendientes.length > 0 && (
                      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center justify-center">
                          <UserCheck className="w-5 h-5 text-yellow-600 mr-2" />
                          <span className="text-yellow-800 font-medium">
                            Tienes {pedidosPendientes.length} pedido{pedidosPendientes.length !== 1 ? 's' : ''} pendiente{pedidosPendientes.length !== 1 ? 's' : ''} de aprobación
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}